import { useEffect, useRef } from "react";

import { Optional } from "common/utils/types";
import { UnsubscribeReason } from "common/types";
import { useFormState } from "common/hooks";
import { FieldErrors } from "common/fetch/types";
import { toPhone } from "common/utils/formatting";
import { PRIMARY_START_SMS_KEYWORD } from "common/constants/inbox";
import {
  ClientPrimaryContactPreferences,
  ClientPrimaryContactPreferencesUpdate,
  MarketingEmailUnsubscribeUpdate,
  TextMessageUnsubscribeUpdate,
} from "common/types/inbox";

import SelectPanel from "client/common/components/inputs/SelectPanel";
import SelectButton from "client/common/components/buttons/SelectButton";
import { unsubscribeReasonOptions as marketingEmailUnsubscribeReasonOptions } from "client/inbox/utils";
import { useClinic } from "client/clinics/hooks";
import TextArea from "client/common/components/inputs/TextArea";
import Select from "client/common/components/inputs/Select";
import Label from "client/common/components/inputs/Label";
import TextButton from "client/common/components/buttons/TextButton";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import {
  useClientPrimaryContactPreferencesQuery,
  useUpdateClientPrimaryContactPreferencesQuery,
} from "client/inbox/hooks";

import ErrorMessage from "../../../common/components/errors/ErrorMessage";
import { deepCombineErrors } from "../../../../common/utils/errors";

import styles from "../../styles/UpdateCommunicationPreferencesForm.module.less";

type FormData = {
  is_subscribed_to_marketing_emails: boolean;
  marketing_email_unsubscribe_reason: UnsubscribeReason | "";
  marketing_email_unsubscribe_reason_other: string;
  is_subscribed_to_messages: boolean;
};

type CommunicationSubscriptionOption = {
  value: boolean;
  label: string;
  description: string;
};

type UpdateCommunicationPreferencesFormContentProps = {
  primaryContactPreferences: ClientPrimaryContactPreferences;
};

type MarketingEmailSubscriptionSelectPanelProps = {
  values: FormData;
  error: Optional<FieldErrors<MarketingEmailUnsubscribeUpdate>>;
  updateField: (field: keyof FormData) => (value: FormData[keyof FormData]) => void;
  clinicName: string;
  isCurrentlySubscribed: boolean;
};

const MarketingEmailSubscriptionSelectPanel = ({
  values,
  error,
  updateField,
  clinicName,
  isCurrentlySubscribed,
}: MarketingEmailSubscriptionSelectPanelProps) => {
  const marketingEmailReasonOtherRef = useRef<HTMLTextAreaElement>(null);

  const isReasonOther = values.marketing_email_unsubscribe_reason === UnsubscribeReason.Other;

  useEffect(() => {
    if (isReasonOther) {
      marketingEmailReasonOtherRef.current?.focus();
    } else {
      updateField("marketing_email_unsubscribe_reason_other")("");
    }
  }, [isReasonOther, updateField]);

  const getSubscriptionOptionProps = (isSubscribed: boolean) => {
    return isSubscribed
      ? {
          label: "Receive marketing emails",
          description: "Receive discounts, deals, and reminders to your email inbox.",
        }
      : {
          label: "Opt out of marketing emails",
          description:
            "Transactional emails such as order and prescription notifications will still be sent to your email address.",
        };
  };

  const renderReasonInputs = () => {
    return (
      <div className={styles.reason}>
        <div className={styles.reasonBody}>
          We&apos;re sorry to see you go! If you have a moment, please let us know why you are
          unsubscribing:
        </div>
        <Select
          label="Reason"
          value={values.marketing_email_unsubscribe_reason}
          onChange={(r) =>
            updateField("marketing_email_unsubscribe_reason")(r as UnsubscribeReason | "")
          }
          errors={error?.marketing_email_unsubscribe?.reason}
        >
          {marketingEmailUnsubscribeReasonOptions(clinicName).map(({ label, value }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
        {isReasonOther && (
          <TextArea
            ref={marketingEmailReasonOtherRef}
            label="I am unsubscribing because..."
            value={values.marketing_email_unsubscribe_reason_other}
            onChange={updateField("marketing_email_unsubscribe_reason_other")}
            errors={error?.marketing_email_unsubscribe?.reason_other}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <SelectPanel<CommunicationSubscriptionOption>
        label="Marketing emails"
        getKey={(option) => option?.value ?? ""}
        options={[
          {
            value: true,
            ...getSubscriptionOptionProps(true),
          },
          {
            value: false,
            ...getSubscriptionOptionProps(false),
          },
        ]}
        getState={({ value }) => ({
          // if has unsubscribed selected, and not previously unsubscribed, show reason
          renderExpandedContent: !value && isCurrentlySubscribed ? renderReasonInputs : undefined,
        })}
        renderOption={({ label, description }, { isActive }) => (
          <SelectButton.DefinitionContent
            status={<div />}
            isActive={isActive}
            label={label}
            description={description}
            align="start"
          />
        )}
        value={{
          value: values.is_subscribed_to_marketing_emails,
          ...getSubscriptionOptionProps(values.is_subscribed_to_marketing_emails),
        }}
        onChange={({ value }) => updateField("is_subscribed_to_marketing_emails")(value)}
      />
      {error?.non_field_errors &&
        error?.non_field_errors.map((msg) => <ErrorMessage key={msg}>{msg}</ErrorMessage>)}
    </div>
  );
};

type MessageSubscriptionSelectPanelProps = {
  primaryContactPreferences: ClientPrimaryContactPreferences;
  values: FormData;
  error: Optional<FieldErrors<TextMessageUnsubscribeUpdate>>;
  updateField: (field: keyof FormData) => (value: FormData[keyof FormData]) => void;
};

const MessageSubscriptionSelectPanel = ({
  primaryContactPreferences,
  values,
  error,
  updateField,
}: MessageSubscriptionSelectPanelProps) => {
  const errorList = deepCombineErrors(error);
  const clinic = useClinic();
  const label = "SMS updates";
  const clinicPhoneDisplay = clinic.phone_number
    ? toPhone(clinic.phone_number)
    : "your veterinarian";
  if (
    primaryContactPreferences.primary_contact_number &&
    primaryContactPreferences.primary_contact_number.is_hard_unsubscribed
  ) {
    return (
      <div className={styles.messageSubscriptionCannotSubscribe}>
        <Label>{label}</Label>
        <div className={styles.messageSubscriptionCannotSubscribeDescription}>
          You&apos;ve responded to us that you don&apos;t want to receive SMS messages for your
          phone number{" "}
          <strong>{primaryContactPreferences.primary_contact_number?.number_display}</strong>.{" "}
          {clinic.communication_number ? (
            <>
              To receive SMS notifications and communications from your veterinary team, please
              message <strong>{PRIMARY_START_SMS_KEYWORD}</strong> to{" "}
              <TextButton href={`sms:${clinic.communication_number.number}`} hasUnderline={true}>
                {toPhone(clinic.communication_number.number)}
              </TextButton>
              .
            </>
          ) : (
            <>
              To receive SMS notifications and communications from your veterinary team, please call{" "}
              {clinicPhoneDisplay}
            </>
          )}
        </div>
        {errorList.map((e) => (
          <ErrorMessage key={e}>{e}</ErrorMessage>
        ))}
      </div>
    );
  }

  const getSubscriptionOptionProps = (isSubscribed: boolean) => {
    return isSubscribed
      ? {
          label: "Receive SMS Updates",
          description: "You may receive automated order, refill, or autoship updates.",
        }
      : {
          label: "Opt out of SMS Updates",
          description:
            "Only messages sent by your veterinary team for important prescription information will still be sent to your mobile number.",
        };
  };

  return (
    <div>
      <SelectPanel<CommunicationSubscriptionOption>
        label={label}
        getKey={(option) => option?.value ?? ""}
        options={[
          { value: true, ...getSubscriptionOptionProps(true) },
          { value: false, ...getSubscriptionOptionProps(false) },
        ]}
        renderOption={({ label: l, description }, { isActive }) => (
          <SelectButton.DefinitionContent
            status={<div />}
            isActive={isActive}
            label={l}
            description={description}
            align="start"
          />
        )}
        value={{
          value: values.is_subscribed_to_messages,
          ...getSubscriptionOptionProps(values.is_subscribed_to_messages),
        }}
        onChange={({ value }) => updateField("is_subscribed_to_messages")(value)}
      />
      {errorList.map((e) => (
        <ErrorMessage key={e}>{e}</ErrorMessage>
      ))}
    </div>
  );
};

const UpdateCommunicationPreferencesFormContent = ({
  primaryContactPreferences,
}: UpdateCommunicationPreferencesFormContentProps) => {
  const { close } = useCurrentDrawer();
  const marketingEmailUnsubscribe =
    primaryContactPreferences?.primary_contact_email?.marketing_email_unsubscribe;
  const [values, updateField] = useFormState<FormData>({
    is_subscribed_to_marketing_emails: !marketingEmailUnsubscribe,
    marketing_email_unsubscribe_reason: marketingEmailUnsubscribe?.reason || "",
    marketing_email_unsubscribe_reason_other: marketingEmailUnsubscribe?.reason_other || "",
    is_subscribed_to_messages: !!primaryContactPreferences?.primary_contact_number?.is_subscribed,
  });

  const {
    mutate: updatePrimaryContactPreferences,
    isLoading,
    isSuccess,
    error,
  } = useUpdateClientPrimaryContactPreferencesQuery({
    onSuccess: close,
  });
  const { display_name: clinicName } = useClinic();

  const isReasonOther = values.marketing_email_unsubscribe_reason === UnsubscribeReason.Other;

  const canSubmit = !!(
    !isLoading &&
    !isSuccess &&
    (values.is_subscribed_to_marketing_emails ||
      !isReasonOther ||
      values.marketing_email_unsubscribe_reason_other)
  );
  const submitValue: ClientPrimaryContactPreferencesUpdate = {};
  if (primaryContactPreferences.primary_contact_email) {
    // only if the client has a primary contact email that we wanna update the email prefs
    submitValue.primary_contact_email = {
      marketing_email_unsubscribe: values.is_subscribed_to_marketing_emails
        ? null
        : {
            reason: values.marketing_email_unsubscribe_reason,
            reason_other: values.marketing_email_unsubscribe_reason_other,
          },
    };
  }
  if (primaryContactPreferences.primary_contact_number) {
    // only if the client has a primary contact number that we wanna update the number prefs
    submitValue.primary_contact_number = {
      is_unsubscribed: !values.is_subscribed_to_messages,
    };
  }
  const submit = () => {
    updatePrimaryContactPreferences(submitValue);
  };

  return (
    <DrawerLayout>
      <DrawerLayout.Header title="Update communication preferences" onClose={close} />
      <DrawerLayout.Form
        submitText="Update"
        canSubmit={canSubmit}
        onSubmit={submit}
        isLoading={isLoading}
        error={error}
      >
        <div className={styles.form}>
          {!!primaryContactPreferences.primary_contact_email && (
            <MarketingEmailSubscriptionSelectPanel
              values={values}
              updateField={updateField}
              isCurrentlySubscribed={!marketingEmailUnsubscribe}
              error={error?.errors?.primary_contact_email}
              clinicName={clinicName}
            />
          )}
          {!!primaryContactPreferences.primary_contact_number && (
            <MessageSubscriptionSelectPanel
              primaryContactPreferences={primaryContactPreferences}
              values={values}
              updateField={updateField}
              error={error?.errors?.primary_contact_number}
            />
          )}
        </div>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const AccountUpdateCommunicationPreferencesDrawer = () => {
  const {
    data: primaryContactPreferences,
    isLoading: isPreferencesLoading,
    error: preferencesError,
  } = useClientPrimaryContactPreferencesQuery();
  if (isPreferencesLoading) {
    return <DrawerLayout.Loading />;
  }
  if (!primaryContactPreferences || preferencesError) {
    return <DrawerLayout.FetchError error={preferencesError} entityName="preferences" />;
  }

  return (
    <UpdateCommunicationPreferencesFormContent
      primaryContactPreferences={primaryContactPreferences}
    />
  );
};

export default AccountUpdateCommunicationPreferencesDrawer;
